import { toast } from "./toast";
import trapFocus from "./focus-trap";

export default function ShareModal() {
  const shareBtn = document.querySelector(".js-share-btn");
  const shareModal = document.querySelector(".js-share-modal");
  const closeBtns = document.querySelectorAll(".js-share-modal-close-btn");
  const shareOverlay = document.querySelector(".js-share-overlay");
  const copyLinkBtn = document.querySelector(".js-share-copy-link");

  if (!shareBtn || !shareModal) return;

  const sendByEmailBtn = shareModal.querySelector(".js-send-by-email");
  const shareForm = shareModal.querySelector(".js-share-form");

  // Held between open and close so the trap can be torn down again. The local trapFocus this
  // replaced never released, so each open stacked another keydown listener on the document.
  let releaseFocus = null;

  shareModal.style.display = "block";
  shareBtn.addEventListener("click", () => {
    shareModal.classList.add("is-open");
    document.body.classList.add("stop-scrolling");

    // The class toggle above only flips visibility/opacity, so layout is already settled — but
    // the original code still deferred the first focus by 10ms, and there is no evidence about
    // which browser needed it. Kept, and the trap's own autoFocus turned off so the two do not
    // race for the first focusable element.
    releaseFocus = trapFocus(shareModal, { onEscape: closeModal, autoFocus: false });
    setTimeout(() => shareModal.querySelector("a")?.focus(), 10);
  });
  closeBtns?.forEach((closeBtn) =>
    closeBtn.addEventListener("click", closeModal)
  );
  shareOverlay?.addEventListener("click", closeModal);

  sendByEmailBtn?.addEventListener("click", () =>
    shareModal.classList.add("is-email")
  );

  shareForm?.addEventListener("submit", onShareFormSubmit);

  copyLinkBtn?.addEventListener("click", onCopyLinkClick);

  function onCopyLinkClick() {
    const shareUrl = copyLinkBtn.dataset.shareUrl;
    navigator.clipboard.writeText(shareUrl);

    toast.success("Link copied successfully!");
  }

  async function onShareFormSubmit(e) {
    e.preventDefault();

    if (!validateShareForm(shareForm)) {
      return;
    }

    clearFieldErrors();

    const action = shareForm.action;
    const method = shareForm.getAttribute("method") || "post";

    try {
      setLoadingState(true);
      const recaptchaSiteKey = shareForm.dataset.recaptchaSiteKey;
      if (recaptchaSiteKey) {
        const token = await grecaptcha.execute(recaptchaSiteKey, {
          action: "submit",
        });
        const tokenInput = shareForm.querySelector("#FormToken");
        tokenInput.value = token;
      }

      const formData = new FormData(shareForm);

      const response = await fetch(action, {
        method: method.toUpperCase(),
        body: formData,
      });

      setLoadingState(false);

      if (!response.ok) {
        // A moderation refusal answers { message, field }; anything else is a bare status.
        const body = await response.json().catch(() => null);

        if (body?.message) {
          showFieldError(body.field, body.message);
          return;
        }

        throw new Error(`Share failed with status ${response.status}`);
      }

      shareModal.classList.add("is-form-success");
    } catch (error) {
      setLoadingState(false);
      console.error(error);
      toast.error("Sorry, that could not be sent. Please try again shortly.");
    }
  }

  // Puts the server's message in the field's own validation span, so it sits beside the input
  // that caused it. A field with no span on the form (or none named) falls back to a toast.
  function showFieldError(field, message) {
    const span = field
      ? shareForm.querySelector(`[data-valmsg-for="${field}"]`)
      : null;

    if (!span) {
      toast.error(message);
      return;
    }

    span.textContent = message;
    span.classList.remove("field-validation-valid");
    span.classList.add("field-validation-error");
    span.setAttribute("role", "alert");
    shareForm.querySelector(`[name="${field}"]`)?.setAttribute("aria-invalid", "true");
  }

  function clearFieldErrors() {
    shareForm.querySelectorAll(".field-validation-error").forEach((span) => {
      span.textContent = "";
      span.classList.remove("field-validation-error");
      span.classList.add("field-validation-valid");
      span.removeAttribute("role");
    });
    shareForm
      .querySelectorAll('[aria-invalid="true"]')
      .forEach((input) => input.removeAttribute("aria-invalid"));
  }

  function setLoadingState(isLoading) {
    isLoading
      ? shareModal.classList.add("is-loading")
      : shareModal.classList.remove("is-loading");
  }

  function closeModal() {
    shareModal.classList.remove("is-open");
    document.body.classList.remove("stop-scrolling");
    releaseFocus?.();
    releaseFocus = null;
    setTimeout(resetFormSteps, 1000);
  }

  function resetFormSteps() {
    shareModal.classList.remove("is-email");
    shareModal.classList.remove("is-form-success");
    setLoadingState(false);
  }

  function validateShareForm(form) {
    const emailInput = form.querySelector('input[type="email"]');
    if (!emailInput) {
      return false;
    }

    const emailValue = emailInput.value.trim();

    if (!emailValue) {
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      return false;
    }

    return true;
  }
}
